import { useState } from "react";
import { useGet } from "@/lib/api";
import { Lock, Check, Clock } from "lucide-react";

const IMPACT = {
  High: "bg-[#EF4444]/15 text-[#EF4444] border-[#EF4444]/40",
  Medium: "bg-[#F59E0B]/15 text-[#F59E0B] border-[#F59E0B]/40",
  Low: "bg-white/5 text-[#9CA3AF] border-white/15",
  Holiday: "bg-white/5 text-[#9CA3AF] border-white/15",
};
const FILTERS = [["all", "All US"], ["hm", "High + Medium"], ["high", "High only"]];
const PASS = { all: () => true, hm: (e) => e.impact === "High" || e.impact === "Medium", high: (e) => e.impact === "High" };

function dayKey(iso) {
  return new Date(iso).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
}
function timeOf(iso) {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export function WeekCalendar({ refreshKey }) {
  const { data, loading } = useGet("/calendar/week", refreshKey);
  const [filter, setFilter] = useState("all");
  if (loading || !data) return <div className="text-center text-[#6B7280] py-6 font-mono-num text-sm">Loading this week…</div>;

  const events = data.events.filter(PASS[filter]);
  const days = events.reduce((acc, e) => { (acc[dayKey(e.date)] ||= []).push(e); return acc; }, {});
  const today = dayKey(new Date().toISOString());

  return (
    <section data-testid="week-calendar" className="mb-8">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <h2 className="font-display text-lg font-semibold">This Week · US Events</h2>
          <p className="text-xs text-[#6B7280] font-mono-num">{events.length} events · times in your local zone · source {data.source}</p>
        </div>
        <div className="flex rounded-lg overflow-hidden border border-white/10 text-xs font-semibold">
          {FILTERS.map(([k, l]) => (
            <button key={k} data-testid={`week-filter-${k}`} onClick={() => setFilter(k)}
              className={`px-3 py-1.5 ${filter === k ? "bg-[#F59E0B] text-black" : "bg-white/5 text-[#9CA3AF]"}`}>{l}</button>
          ))}
        </div>
      </div>

      {events.length === 0 && <div className="glass rounded-2xl p-5 text-sm text-[#9CA3AF]">No matching events this week.</div>}

      <div className="flex flex-col gap-4">
        {Object.entries(days).map(([day, list]) => (
          <div key={day}>
            <div className={`text-[11px] font-mono-num uppercase tracking-wider mb-1.5 ${day === today ? "text-[#F59E0B]" : "text-[#6B7280]"}`}>
              {day}{day === today ? " · today" : ""}
            </div>
            <div className="glass rounded-2xl divide-y divide-white/5">
              {list.map((e) => (
                <div key={`${e.title}-${e.date}`} data-testid={`week-event-${e.title.replace(/\W+/g, "-").toLowerCase()}`}
                  className={`flex items-center gap-3 px-4 py-3 ${e.released ? "opacity-60" : ""}`}>
                  <div className="w-20 shrink-0 font-mono-num text-sm text-[#D1D5DB] whitespace-nowrap">{timeOf(e.date)}</div>
                  <div className="shrink-0">
                    {e.released ? <Check size={14} className="text-[#10B981]" /> : <Clock size={14} className="text-[#6B7280]" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{e.title}</div>
                    <span className={`inline-block mt-0.5 text-[10px] px-1.5 py-0.5 rounded border font-mono-num ${IMPACT[e.impact] || IMPACT.Low}`}>{e.impact}</span>
                  </div>
                  <div className="text-right shrink-0 font-mono-num text-xs">
                    {e.locked ? (
                      <span data-testid="week-event-locked" className="flex items-center gap-1 text-[#F59E0B]"><Lock size={12} /> Pro</span>
                    ) : (
                      <>
                        <div className="text-[#D1D5DB]">Cons. <span className="font-bold">{e.forecast || "—"}</span></div>
                        <div className="text-[#6B7280]">Prev. {e.previous || "—"}</div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
